import type { Student } from '../../../../../utils/firebase';
import { Camera } from './Camera';
import { Box2dPhysics } from './Box2dPhysics';
import { initialZoom, zoomThreshold } from './constantsCamera';
import { stages, type StageDef } from './data/maps';
import { cloneStage } from './stageStorage';
import { canvasHeight, canvasWidth, themeMarbleDark } from './gameConstants';
import { Marble } from './Marble';
import { MarbleRouletteRenderer } from './MarbleRouletteRenderer';

function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

export type MarbleRouletteEngineOptions = {
  canvas: HTMLCanvasElement;
  onGoal: (student: Student) => void;
  /** 기본 내장 맵 인덱스 (0 … stages.length-1) */
  initialBuiltinStageIndex?: number;
};

/**
 * lazygyu Marble Roulette(https://lazygyu.github.io/roulette/) 과 같은 Box2D 트랙·카메라 추적.
 */
export class MarbleRouletteEngine {
  private physics!: Box2dPhysics;
  private readonly camera = new Camera();
  private readonly renderer: MarbleRouletteRenderer;
  private currentStage: StageDef;

  private marbles: Marble[] = [];
  /** marble.id(스폰 슬롯) → 학생 */
  private studentByMarbleId: Student[] = [];

  private lastTime = 0;
  private elapsed = 0;
  private readonly updateInterval = 10;
  private timeScale = 1;
  private speed = 1;

  private winnersInternal: Marble[] = [];
  private winnerRank = 0;
  private totalMarbleCount = 0;
  private goalDist = Infinity;
  private isRunning = false;
  private winner: Marble | null = null;

  private rafId: number | null = null;
  private cancelled = false;
  private ready = false;

  private readonly callbacks: MarbleRouletteEngineOptions;

  constructor(opts: MarbleRouletteEngineOptions) {
    this.callbacks = opts;
    const idx = Math.min(
      Math.max(0, opts.initialBuiltinStageIndex ?? 0),
      Math.max(0, stages.length - 1)
    );
    this.currentStage = cloneStage(stages[idx]!);
    this.renderer = new MarbleRouletteRenderer(opts.canvas);
  }

  getStage(): StageDef {
    return this.currentStage;
  }

  /** 내장 맵으로 전환 (물리·카메라 리셋, 구슬 없음) */
  setBuiltinStageIndex(index: number): void {
    const idx = Math.min(Math.max(0, index), Math.max(0, stages.length - 1));
    this.setStage(cloneStage(stages[idx]!));
  }

  /** 임의 StageDef 적용 (커스텀 맵) */
  setStage(stage: StageDef): void {
    if (!this.physics) {
      this.currentStage = cloneStage(stage);
      return;
    }
    this.clearMarbles();
    this.winnersInternal = [];
    this.winner = null;
    this.isRunning = false;
    this.goalDist = Infinity;
    this.timeScale = 1;
    this.currentStage = cloneStage(stage);
    this.physics.clear();
    this.physics.createStage(this.currentStage);
    this.camera.initializePosition();
  }

  setSpeed(multiplier: number): void {
    if (multiplier <= 0) return;
    this.speed = multiplier;
  }

  getSpeed(): number {
    return this.speed;
  }

  get isReady(): boolean {
    return this.ready;
  }

  async init(): Promise<void> {
    this.renderer.init();
    this.physics = new Box2dPhysics();
    await this.physics.init();
    this.loadMap();
    this.ready = true;
    this.scheduleLoop();
  }

  destroy(): void {
    this.cancelled = true;
    if (this.rafId != null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.physics) {
      this.physics.clearMarbles();
      this.physics.clear();
    }
    this.marbles = [];
    this.studentByMarbleId = [];
  }

  private loadMap(): void {
    this.physics.createStage(this.currentStage);
    this.camera.initializePosition();
  }

  clearMarbles(): void {
    this.physics.clearMarbles();
    this.winner = null;
    this.winnersInternal = [];
    this.marbles = [];
    this.studentByMarbleId = [];
  }

  resetRound(): void {
    this.clearMarbles();
    this.physics.clear();
    this.loadMap();
    this.goalDist = Infinity;
    this.isRunning = false;
    this.timeScale = 1;
    this.winner = null;
    this.winnersInternal = [];
  }

  /** 이번 라운드 참가자. 승자 제외한 목록만 넘기면 됩니다. */
  setParticipants(participants: Student[]): void {
    this.resetRound();
    const totalCount = participants.length;
    if (totalCount === 0) return;

    const orders = shuffle(participants.map((_, i) => i));
    this.studentByMarbleId = new Array(totalCount);

    participants.forEach((student, idx) => {
      const order = orders[idx]!;
      this.studentByMarbleId[order] = student;
      const marble = new Marble(this.physics, order, totalCount, student.name ?? '?', 1);
      this.marbles.push(marble);
    });

    this.totalMarbleCount = totalCount;

    const cols = Math.min(totalCount, 10);
    const rows = Math.ceil(totalCount / 10);
    const lineDelta = -Math.max(0, Math.ceil(rows - 5));
    const centerX = 10.25 + (cols - 1) * 0.3;
    const centerY = (1 + rows) / 2 + lineDelta;

    const spawnWidth = Math.max((cols - 1) * 0.6, 1);
    const spawnHeight = Math.max(rows - 1, 1);
    const margin = 3;
    const viewW = canvasWidth / initialZoom;
    const viewH = canvasHeight / initialZoom;
    const zoom = Math.max(
      1.5,
      Math.min(Math.min(viewW / (spawnWidth + margin * 2), viewH / (spawnHeight + margin * 2)), 3)
    );

    this.camera.initializePosition({ x: centerX, y: centerY }, zoom);
  }

  start(): void {
    if (this.marbles.length === 0) return;
    this.isRunning = true;
    this.winnerRank = 0;
    if (this.winnerRank >= this.marbles.length) {
      this.winnerRank = this.marbles.length - 1;
    }
    this.camera.startFollowingMarbles();
    this.physics.start();
    this.marbles.forEach(m => {
      m.isActive = true;
    });
  }

  private scheduleLoop(): void {
    const tick = () => {
      if (this.cancelled) return;
      this.rafId = requestAnimationFrame(tick);
      this.stepFrame();
    };
    this.rafId = requestAnimationFrame(tick);
  }

  private stepFrame(): void {
    if (!this.ready || !this.physics) return;

    if (!this.lastTime) this.lastTime = Date.now();
    const currentTime = Date.now();

    this.elapsed += (currentTime - this.lastTime) * this.speed;
    if (this.elapsed > 100) {
      this.elapsed %= 100;
    }
    this.lastTime = currentTime;

    const interval = (this.updateInterval / 1000) * this.timeScale;

    while (this.elapsed >= this.updateInterval) {
      this.physics.step(interval);
      this.updateMarbles(this.updateInterval);
      this.elapsed -= this.updateInterval;
    }

    if (this.marbles.length > 1) {
      this.marbles.sort((a, b) => b.y - a.y);
    }

    this.camera.update({
      marbles: this.marbles,
      stage: this.currentStage,
      needToZoom: this.goalDist < zoomThreshold,
      targetIndex: this.winnersInternal.length > 0 ? this.winnerRank - this.winnersInternal.length : 0,
    });

    this.render();
  }

  private updateMarbles(deltaTime: number): void {
    const stage = this.currentStage;
    if (!stage) return;

    for (let i = 0; i < this.marbles.length; i++) {
      const marble = this.marbles[i]!;
      marble.update(deltaTime);

      if (marble.y > stage.goalY) {
        this.winnersInternal.push(marble);

        if (this.isRunning && this.winnersInternal.length === this.winnerRank + 1) {
          const student = this.studentByMarbleId[marble.id];
          if (student) {
            this.callbacks.onGoal(student);
          }
          this.winner = marble;
          this.isRunning = false;
        } else if (
          this.isRunning &&
          this.winnerRank === this.winnersInternal.length &&
          this.winnerRank === this.totalMarbleCount - 1
        ) {
          const next = this.marbles[i + 1];
          const student = next ? this.studentByMarbleId[next.id] : undefined;
          if (student) {
            this.callbacks.onGoal(student);
          }
          if (next) this.winner = next;
          this.isRunning = false;
        }

        window.setTimeout(() => {
          this.physics.removeMarble(marble.id);
        }, 500);
      }
    }

    const targetIndex = this.winnerRank - this.winnersInternal.length;
    const topY = this.marbles[targetIndex] ? this.marbles[targetIndex]!.y : 0;
    this.goalDist = Math.abs(stage.zoomY - topY);
    this.timeScale = this.calcTimeScale(targetIndex);

    this.marbles = this.marbles.filter(m => m.y <= stage.goalY);
  }

  private calcTimeScale(targetIndex: number): number {
    if (!this.currentStage) return 1;
    if (this.winnersInternal.length < this.winnerRank + 1 && this.goalDist < zoomThreshold) {
      const leader = this.marbles[targetIndex];
      if (
        leader &&
        leader.y > this.currentStage.zoomY - zoomThreshold * 1.2 &&
        (this.marbles[targetIndex - 1] || this.marbles[targetIndex + 1])
      ) {
        return Math.max(0.2, this.goalDist / zoomThreshold);
      }
    }
    return 1;
  }

  private render(): void {
    if (!this.currentStage) return;
    this.renderer.render({
      camera: this.camera,
      stage: this.currentStage,
      entities: this.physics.getEntities(),
      marbles: this.marbles,
      winners: this.winnersInternal,
      winnerRank: this.winnerRank,
      winner: this.winner,
      size: { x: this.renderer.width, y: this.renderer.height },
      theme: themeMarbleDark,
    });
  }
}
